import { FlashList, FlashListRef } from "@shopify/flash-list";
import { AxiosError } from "axios";
import { useIsFocused } from "expo-router";
import { RefObject, useContext, useEffect, useRef } from "react";

import type { SaveNotificationToConversationRequest } from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model";
import {
  useMarkConversationAsRead,
  useSaveNotificationToConversation,
} from "@/api/mutations/conversation";
import {
  ConstrainedView,
  Paragraph,
  Spacer,
  XStack,
  YStack,
  getTokenValue,
} from "@/components";
import ChatInput from "@/components/ChatInput";
import ImageGallery from "@/components/ImageGallery";
import { LayoutContext } from "@/components/LayoutProvider";
import { isDev, isLocal } from "@/config/env";
import AskUserQuestionsPanel from "@/conversation/AskUserQuestionsPanel";
import ConversationClosedNote from "@/conversation/ConversationClosedNote";
import FeedbackCommentModal from "@/conversation/FeedbackCommentModal";
import MedicationHistoryPanel from "@/conversation/MedicationHistory";
import NoriTurn from "@/conversation/NoriTurn";
import PreAsyncSetupPanel from "@/conversation/PreAsyncSetup";
import { usePreAsyncSetupSession } from "@/conversation/PreAsyncSetup/usePreAsyncSetupSession";
import PreClinicStepsPanel from "@/conversation/PreClinicSteps";
import ProgramEnrollmentGate from "@/conversation/PreClinicSteps/ProgramEnrollmentGate";
import RxSurveyPanel from "@/conversation/RxSurveyPanel";
import SmsConsentNudge from "@/conversation/SmsConsentNudge";
import TalkToDoctorAINudge from "@/conversation/TalkToDoctorAINudge";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import {
  useCurrentNotificationId,
  useIsCurrentConversationClosed,
} from "@/hooks/conversation";
import { useConversationStream } from "@/hooks/useConversationStream";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import useRedirectUrl from "@/hooks/useRedirectUrl";
import { isGeneralistRxFlow, useRxSurvey } from "@/hooks/useRxSurvey";
import { useSignupNudge } from "@/hooks/useSignupNudge";
import OutreachNudge from "@/memory/OutreachNudge";
import { setTag } from "@/sentry";
import { useAskUserQuestionsSession } from "@/store/askUserQuestions";
import { useConversationComposition } from "@/store/conversation";
import { useEarlyPreClinicSession } from "@/store/preClinicSteps";
import { getBrowserInfo } from "@/utils/web";

function RxComposerLocked({ message }: { message: string }) {
  return (
    <ConstrainedView>
      <XStack
        alignItems="center"
        paddingHorizontal="$250"
        paddingVertical="$250"
        borderRadius="$300"
        borderWidth="$strokeWidth"
        borderColor="$strokeNeutral"
        backgroundColor="$fillNeutral"
        opacity={0.7}
      >
        <Paragraph color="$textSecondary">{message}</Paragraph>
      </XStack>
    </ConstrainedView>
  );
}

export const ChatInputWrapper = ({
  isCurrentConversationClosed,
  onSubmitEditing,
  rxLocked = false,
  rxLockMessage = "Respond above to continue",
}: {
  isCurrentConversationClosed: boolean | null | undefined;
  onSubmitEditing?: () => void;
  rxLocked?: boolean;
  rxLockMessage?: string;
}) => {
  return (
    <>
      {isCurrentConversationClosed ? (
        <ConversationClosedNote />
      ) : rxLocked ? (
        <RxComposerLocked message={rxLockMessage} />
      ) : (
        <ChatInput
          constrained
          key="chat-input-conversation"
          dataTrackingId="chat-input"
          extraTrackingContext={{ messageType: "nori" }}
          enableGradient
          onSubmitEditing={onSubmitEditing}
        />
      )}
    </>
  );
};

export default function NoriConversation({
  conversationId,
}: {
  conversationId: string;
}) {
  const { browser, platform } = getBrowserInfo() ?? {};
  const router = usePropagatedRouter();
  const isFocused = useIsFocused();
  const hasAutoScrolledConversationIdRef = useRef<string | null>(null);
  const notificationId = useCurrentNotificationId();
  const isCurrentConversationClosed = useIsCurrentConversationClosed();
  const rxSurvey = useRxSurvey(conversationId);
  const { mutate: saveNotificationToConversation } =
    useSaveNotificationToConversation();
  const { nudgeType } = useSignupNudge();
  const { track } = useAnalytics();
  const { location, pathname } = useEventContext();
  const isSignedInStable = useIsSignedInStable();
  const redirectUrl = useRedirectUrl();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: getMessagesError,
  } = useConversationStream(conversationId);

  const { setAttachmentCount, setAttachmentSize } =
    useConversationComposition();

  useEffect(() => {
    if (getMessagesError && (getMessagesError as AxiosError).status === 404) {
      if (isSignedInStable) {
        router.navigate("/");
      } else {
        router.replace({ pathname: "/sign-in", params: { redirectUrl } });
      }
    }
  }, [getMessagesError, router, redirectUrl, isSignedInStable]);

  useEffect(() => {
    if (conversationId && notificationId && isSignedInStable) {
      saveNotificationToConversation({
        data: {
          conversation_id: conversationId,
          notification_id: notificationId,
        } as SaveNotificationToConversationRequest,
      });
    }
  }, [
    conversationId,
    notificationId,
    isSignedInStable,
    saveNotificationToConversation,
  ]);

  const { setShowHeader, lg, chatScrollViewRef, setChatScrollViewHeight } =
    useContext(LayoutContext);

  const { allMessages = [], allTurns = [] } = data ?? {};
  // Per-conversation: only the clinical-ai-v1-rx generalist rx flow gets the new transcript rendering.
  const isGeneralistRx = isGeneralistRxFlow(allMessages);
  const conversation = data?.pages[0]?.conversation;

  useAskUserQuestionsSession(allMessages);
  useEarlyPreClinicSession(allMessages, conversationId);
  usePreAsyncSetupSession(data?.allMessages, conversationId);

  useEffect(() => {
    if (conversation) {
      setAttachmentCount(conversation.attachment_count ?? 0);
      setAttachmentSize(conversation.total_attachment_size ?? 0);
    }
  }, [conversation, setAttachmentCount, setAttachmentSize]);

  const { mutate: markConversationAsRead } = useMarkConversationAsRead(
    conversation?.conversation_group_id,
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies(allMessages.length): used to trigger the effect
  useEffect(() => {
    if (conversation?.id) {
      markConversationAsRead({ conversationId: conversation.id });
    }
  }, [allMessages.length, conversation?.id, markConversationAsRead]);

  useEffect(() => {
    setTag("conversation_id", conversationId);
  }, [conversationId]);

  useEffect(() => {
    if (!data || hasAutoScrolledConversationIdRef.current === conversationId) {
      return;
    }
    chatScrollViewRef.current?.scrollToEnd({ animated: false });
    hasAutoScrolledConversationIdRef.current = conversationId;
  }, [chatScrollViewRef, conversationId, data]);

  useEffect(() => {
    setShowHeader(true);
  }, [setShowHeader]);

  useEffect(() => {
    if (nudgeType) {
      track(`superclinical-${location}__soft-nudge-shown`, {
        event_context: {
          pathname,
          location,
          nudge_type: nudgeType,
          conversation_id: conversationId,
        },
      });
    }
  }, [nudgeType, location, pathname, conversationId, track]);

  return (
    <>
      <YStack
        flex={1}
        position="relative"
        onLayout={(e) => {
          // Expo Router keeps previous screens mounted on the stack, so we need to check if the screen is focused
          if (isFocused) {
            const height = e.nativeEvent.layout.height;
            setChatScrollViewHeight(height);
          }
        }}
      >
        <FlashList
          data={allTurns}
          ref={chatScrollViewRef as RefObject<FlashListRef<ClientEventModel[]>>}
          renderItem={({ item, index }) => (
            <NoriTurn
              turn={item}
              index={index}
              isLatest={index === allTurns.length - 1}
              hiddenMessageIds={
                isGeneralistRx ? rxSurvey.hiddenMessageIds : undefined
              }
              isGeneralistRx={isGeneralistRx}
            />
          )}
          extraData={[
            allTurns.length,
            isGeneralistRx,
            rxSurvey.hiddenMessageIds,
          ]}
          ItemSeparatorComponent={() => <Spacer size="$200" />}
          keyExtractor={(item) => {
            const baseKey =
              item[0].role === "user"
                ? (item[0].clientId ?? item[0].id)
                : item[0].id;
            return `${baseKey}-${item.length}`;
          }}
          maintainVisibleContentPosition={{
            startRenderingFromBottom: true,
            animateAutoScrollToBottom: false,
          }}
          onStartReached={() => {
            if (!hasNextPage || isFetchingNextPage) return;
            fetchNextPage();
          }}
          onStartReachedThreshold={0.1}
          scrollEventThrottle={16}
          contentContainerStyle={{
            flexGrow: 1,
            flexShrink: 0,
            width: "100%",
            maxWidth: getTokenValue("$constraint"),
            alignSelf: "center",
            paddingHorizontal: getTokenValue("$screenPadding"),
            ...(lg && {
              boxSizing: "content-box",
              // 8px is reserved on lg devices for scrollbar gutter
              paddingHorizontal: getTokenValue("$100"),
              // Safari doesn't respect scrollbar gutter, manually correct for it
              ...(browser?.name === "Safari" &&
                platform?.type === "desktop" && {
                  marginLeft: getTokenValue("$250"),
                }),
            }),
          }}
          overrideProps={{
            style: {
              scrollbarGutter: "stable both-edges",
              ...(lg && {
                paddingHorizontal: getTokenValue("$100"),
                ...(browser?.name === "Safari" &&
                  platform?.type === "desktop" && {
                    paddingHorizontal: getTokenValue("$200"),
                  }),
              }),
            },
          }}
        />
        <YStack
          position="absolute"
          bottom="$200"
          left={0}
          right={0}
          zIndex={10}
          pointerEvents="box-none"
        >
          <AskUserQuestionsPanel />
          <ProgramEnrollmentGate />
          <PreAsyncSetupPanel />
          <MedicationHistoryPanel />
          <PreClinicStepsPanel />
          {isGeneralistRx && <RxSurveyPanel />}
        </YStack>
      </YStack>
      {nudgeType === "outreach" && <OutreachNudge />}
      <SmsConsentNudge conversationId={conversationId} />
      <TalkToDoctorAINudge conversationId={conversationId} />
      <ChatInputWrapper
        isCurrentConversationClosed={isCurrentConversationClosed}
        rxLocked={isGeneralistRx && rxSurvey.composerLocked}
      />
      <ImageGallery />
      {(isLocal || isDev) && <FeedbackCommentModal />}
    </>
  );
}
